import React, { useState, useEffect, useRef } from "react";
import {
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
} from "react-icons/fi";

function convertTo12HourFormat(time24h) {
  const [hours24, minutes] = time24h.split(":");
  let hours = parseInt(hours24, 10);
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  // The hour '0' should be '12' in 12-hour format
  hours = hours === 0 ? 12 : hours;

  return `${hours}:${minutes} ${ampm}`;
}

export const DateTimeSelectionSection = ({
  formData,
  errors,
  onChange,
  weekSlots = {}, // { "2025-01-15": [...slots], "2025-01-16": [...slots] }
  loadingSlots,
  onWeekChange, // Callback to load slots for a week
}) => {
  // ✅ HELPER: Convert date to local YYYY-MM-DD string (no timezone issues)
  const toLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ✅ HELPER: Parse YYYY-MM-DD string to local date object
  const parseLocalDate = (dateString) => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  // State for current week start date
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    // ✅ START FROM TODAY (not tomorrow)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Get start of week (Monday)
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + diff);
    return weekStart;
  });

  // ✅ Track if we've auto-selected for current week/duration combination
  const lastAutoSelectKey = useRef(null);

  // State for time slot view mode (grid or dropdown)
  const [timeViewMode, setTimeViewMode] = useState("grid"); // "grid" or "dropdown"

  // Touch/Swipe handling for mobile
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const weekContainerRef = useRef(null);

  // Generate dates for current week
  const getWeekDates = (startDate) => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates(currentWeekStart);

  // ✅ Get minimum date - TODAY (not tomorrow)
  const getMinDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Get maximum date (30 days from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    maxDate.setHours(0, 0, 0, 0);
    return maxDate;
  };

  const minDate = getMinDate();
  const maxDate = getMaxDate();

  // Check if date is in valid range
  const isDateValid = (date) => {
    return date >= minDate && date <= maxDate;
  };

  // Filter valid dates
  const validWeekDates = weekDates.filter(isDateValid);

  // ✅ SMART DATE SELECTION LOGIC
  const selectBestAvailableDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = toLocalDateString(today);

    // Create auto-select key to track if we've already done this for this week/duration
    const autoSelectKey = `${toLocalDateString(currentWeekStart)}-${
      formData.duration
    }`;

    // If we already auto-selected for this combination, don't do it again
    if (lastAutoSelectKey.current === autoSelectKey) {
      return;
    }

    console.log("🎯 Running smart date selection...");

    // Get all dates in current week with their slot counts
    const datesWithSlots = validWeekDates
      .map((date) => {
        const dateStr = toLocalDateString(date);
        const slots = weekSlots[dateStr] || [];
        return {
          dateStr,
          slotsCount: slots.length,
          date,
          isToday: dateStr === todayStr,
        };
      })
      .filter((item) => item.slotsCount > 0) // Only dates with available slots
      .sort((a, b) => a.date - b.date); // Sort by date (earliest first)

    console.log("📊 Dates with slots:", datesWithSlots);

    // Priority 1: If today has slots, select today
    const todayWithSlots = datesWithSlots.find((item) => item.isToday);
    if (todayWithSlots) {
      console.log(
        `✅ Today has ${todayWithSlots.slotsCount} slots - selecting today`
      );
      onChange({
        target: {
          name: "selectedDate",
          value: todayStr,
        },
      });
      lastAutoSelectKey.current = autoSelectKey;
      return;
    }

    // Priority 2: If today has no slots, select the closest future date with slots
    if (datesWithSlots.length > 0) {
      const closestDate = datesWithSlots[0];
      console.log(
        `✅ Today has no slots - selecting closest available: ${closestDate.dateStr} (${closestDate.slotsCount} slots)`
      );

      onChange({
        target: {
          name: "selectedDate",
          value: closestDate.dateStr,
        },
      });
      lastAutoSelectKey.current = autoSelectKey;
      return;
    }

    // Priority 3: No slots in current week - select today anyway but show empty state
    console.log(
      "⚠️ No slots available in current week - keeping today selected, user will see empty state"
    );
    if (!formData.selectedDate) {
      onChange({
        target: {
          name: "selectedDate",
          value: todayStr,
        },
      });
    }
    lastAutoSelectKey.current = autoSelectKey;
  };

  // ✅ Initial mount: Set today as default
  useEffect(() => {
    if (!formData.selectedDate) {
      const today = new Date();
      const todayStr = toLocalDateString(today);
      console.log("📅 Setting initial date to today:", todayStr);
      onChange({
        target: {
          name: "selectedDate",
          value: todayStr,
        },
      });
    }

    // Load slots for current week
    if (onWeekChange && validWeekDates.length > 0) {
      const dateStrings = validWeekDates.map((d) => toLocalDateString(d));
      console.log("📅 Loading initial slots for dates:", dateStrings);
      onWeekChange(dateStrings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Smart auto-selection when slots are loaded or week/duration changes
  useEffect(() => {
    // Wait for slots to load
    if (loadingSlots || Object.keys(weekSlots).length === 0) {
      return;
    }

    // Check if current selected date has slots
    const currentSlots = formData.selectedDate
      ? weekSlots[formData.selectedDate] || []
      : [];

    const currentAutoSelectKey = `${toLocalDateString(currentWeekStart)}-${
      formData.duration
    }`;
    const needsAutoSelect = lastAutoSelectKey.current !== currentAutoSelectKey;

    // Auto-select if:
    // 1. We haven't auto-selected for this week/duration combination yet
    // 2. OR current selected date has no slots
    if (needsAutoSelect || currentSlots.length === 0) {
      selectBestAvailableDate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekSlots, loadingSlots, currentWeekStart, formData.duration]);

  // ✅ Load slots when week OR duration changes
  useEffect(() => {
    if (onWeekChange && validWeekDates.length > 0 && formData.duration) {
      const dateStrings = validWeekDates.map((d) => toLocalDateString(d));
      console.log(
        "🔄 Reloading slots due to week/duration change:",
        dateStrings
      );

      // Reset auto-selection flag to allow re-selection for new week/duration
      lastAutoSelectKey.current = null;

      onWeekChange(dateStrings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart, formData.duration]);

  // Navigate to previous week
  const handlePreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() - 7);

    if (
      newWeekStart >= minDate ||
      getWeekDates(newWeekStart).some(isDateValid)
    ) {
      setCurrentWeekStart(newWeekStart);
      // Reset auto-select flag to allow auto-selection in new week
      lastAutoSelectKey.current = null;
    }
  };

  // Navigate to next week
  const handleNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + 7);

    if (newWeekStart <= maxDate) {
      setCurrentWeekStart(newWeekStart);
      // Reset auto-select flag to allow auto-selection in new week
      lastAutoSelectKey.current = null;
    }
  };

  // MOBILE: Touch handlers for swipe gestures
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50; // Minimum swipe distance
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next week
        handleNextWeek();
      } else {
        // Swipe right - previous week
        handlePreviousWeek();
      }
    }
  };

  // Check if navigation buttons should be disabled
  const isPrevWeekDisabled = () => {
    const prevWeekStart = new Date(currentWeekStart);
    prevWeekStart.setDate(currentWeekStart.getDate() - 7);
    return !getWeekDates(prevWeekStart).some(isDateValid);
  };

  const isNextWeekDisabled = () => {
    const nextWeekStart = new Date(currentWeekStart);
    nextWeekStart.setDate(currentWeekStart.getDate() + 7);
    return nextWeekStart > maxDate;
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    const dateStr = toLocalDateString(date);
    onChange({
      target: {
        name: "selectedDate",
        value: dateStr,
      },
    });

    // Clear selected time when date changes
    if (formData.selectedTime) {
      onChange({
        target: {
          name: "selectedTime",
          value: "",
        },
      });
    }
  };

  // Format date display
  const formatDate = (date) => {
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  const formatDay = (date) => {
    return date.toLocaleDateString("en-IN", { weekday: "short" });
  };

  // Get slots for selected date
  const selectedDateSlots = formData.selectedDate
    ? weekSlots[formData.selectedDate] || []
    : [];

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <FiCalendar className="text-primary text-xl" />
          <h3 className="text-lg font-semibold text-text">
            Select Date & Time
          </h3>
        </div>

        {/* Duration Selection - Full width on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center gap-2 flex-1">
            <FiClock className="text-neutral-dark flex-shrink-0" />
            <select
              name="duration"
              value={formData.duration}
              onChange={onChange}
              className={`flex-1 sm:flex-initial px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm min-w-[120px] ${
                errors.duration ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select duration</option>
              <option value="20">20 min</option>
              <option value="30">30 min</option>
              <option value="45">45 min</option>
              <option value="60">60 min</option>
              <option value="90">90 min</option>
            </select>
          </div>
          {errors.duration && (
            <p className="text-xs text-red-500 sm:hidden">{errors.duration}</p>
          )}
        </div>
      </div>

      {/* Duration error for desktop */}
      {errors.duration && (
        <p className="hidden sm:block text-sm text-red-500 -mt-2 mb-4">
          {errors.duration}
        </p>
      )}

      {/* Duration selection prompt */}
      {!formData.duration && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-800">
            👆 <strong>Please select a duration above</strong> to see available
            time slots
          </p>
        </div>
      )}

      {/* Week Calendar - Mobile Optimized */}
      <div className="mb-4 sm:mb-6">
        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          {/* Previous Week Button - Larger on mobile */}
          <button
            type="button"
            onClick={handlePreviousWeek}
            disabled={isPrevWeekDisabled()}
            className="p-3 sm:p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors touch-manipulation"
            aria-label="Previous week"
          >
            <FiChevronLeft className="w-6 h-6 sm:w-5 sm:h-5" />
          </button>

          {/* Week Range Display */}
          <div className="text-center flex-1 px-2">
            <p className="text-sm sm:text-base font-medium text-text">
              {formatDate(validWeekDates[0])} -{" "}
              {formatDate(validWeekDates[validWeekDates.length - 1])}
            </p>
            <p className="text-xs text-neutral-dark mt-0.5">
              {currentWeekStart.toLocaleDateString("en-IN", {
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>

          {/* Next Week Button - Larger on mobile */}
          <button
            type="button"
            onClick={handleNextWeek}
            disabled={isNextWeekDisabled()}
            className="p-3 sm:p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors touch-manipulation"
            aria-label="Next week"
          >
            <FiChevronRight className="w-6 h-6 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Week Days Grid - 2 rows on mobile, 1 row on desktop */}
        <div
          ref={weekContainerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="grid grid-cols-4 sm:grid-cols-7 gap-2 sm:gap-2"
        >
          {validWeekDates.map((date, index) => {
            const dateStr = toLocalDateString(date);
            const slots = weekSlots[dateStr] || [];
            const isSelected = formData.selectedDate === dateStr;
            const isToday = new Date().toDateString() === date.toDateString();
            const hasSlots = slots.length > 0;

            return (
              <button
                key={dateStr}
                type="button"
                onClick={() => handleDateSelect(date)}
                className={`relative p-3 sm:p-3 rounded-lg border-2 transition-all hover:shadow-md touch-manipulation active:scale-95
                  ${
                    isSelected
                      ? "border-primary bg-primary text-white shadow-lg scale-105"
                      : hasSlots
                      ? "border-green-200 bg-green-50 hover:border-green-400"
                      : "border-gray-200 bg-gray-50 hover:border-gray-300"
                  }
                  ${index >= 4 ? "sm:col-span-1" : ""}`}
              >
                {/* Day */}
                <div
                  className={`text-xs sm:text-xs font-medium mb-1 ${
                    isSelected ? "text-white" : "text-neutral-dark"
                  }`}
                >
                  {formatDay(date)}
                </div>

                {/* Date */}
                <div
                  className={`text-lg sm:text-lg font-bold mb-1 ${
                    isSelected ? "text-white" : "text-text"
                  }`}
                >
                  {date.getDate()}
                </div>

                {/* Slot Count Badge */}
                {loadingSlots ? (
                  <div className="h-5 sm:h-5 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                  </div>
                ) : hasSlots ? (
                  <div
                    className={`text-xs font-semibold truncate ${
                      isSelected ? "text-white" : "text-green-600"
                    }`}
                  >
                    {slots.length} slots
                  </div>
                ) : (
                  <div
                    className={`text-xs truncate ${
                      isSelected ? "text-white/70" : "text-gray-400"
                    }`}
                  >
                    No slots
                  </div>
                )}

                {/* Today Indicator */}
                {isToday && !isSelected && (
                  <div className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>

        {errors.selectedDate && (
          <p className="mt-2 text-sm text-red-500">{errors.selectedDate}</p>
        )}

        {/* MOBILE: Swipe hint */}
        <p className="text-xs text-neutral-dark text-center mt-3 sm:hidden">
          👆 Swipe left/right to change weeks
        </p>
      </div>

      {/* Time Slots for Selected Date - Mobile Optimized */}
      {formData.selectedDate && (
        <div className="border-t pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 sm:mb-4">
            <h4 className="text-base sm:text-md font-semibold text-text">
              Available Time Slots
            </h4>
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-dark">
                {/* ✅ Parse date string to avoid timezone issues */}
                {parseLocalDate(formData.selectedDate).toLocaleDateString(
                  "en-IN",
                  {
                    weekday: "long",
                    day: "numeric",
                    month: "short",
                  }
                )}
              </span>
            </div>
          </div>

          {/* View Mode Toggle - Show only when slots are available */}
          {!loadingSlots && selectedDateSlots.length > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-neutral-dark">View:</span>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setTimeViewMode("grid")}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    timeViewMode === "grid"
                      ? "bg-primary text-white"
                      : "bg-white text-neutral-dark hover:bg-gray-50"
                  }`}
                >
                  Grid
                </button>
                <button
                  type="button"
                  onClick={() => setTimeViewMode("dropdown")}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-300 ${
                    timeViewMode === "dropdown"
                      ? "bg-primary text-white"
                      : "bg-white text-neutral-dark hover:bg-gray-50"
                  }`}
                >
                  Dropdown
                </button>
              </div>
            </div>
          )}

          {loadingSlots ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="ml-3 text-neutral-dark">Loading slots...</p>
            </div>
          ) : selectedDateSlots.length > 0 ? (
            <div>
              <p className="text-sm text-neutral-dark mb-3">
                {selectedDateSlots.length} slot
                {selectedDateSlots.length !== 1 ? "s" : ""} available
              </p>

              {/* GRID VIEW - Responsive grid */}
              {timeViewMode === "grid" && (
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 max-h-64 overflow-y-auto overscroll-contain">
                  {selectedDateSlots.map((slot) => (
                    <label
                      key={slot.start}
                      className={`cursor-pointer rounded-lg border-2 p-2.5 sm:p-3 text-center transition-all hover:shadow-md touch-manipulation active:scale-95
                        ${
                          formData.selectedTime === slot.start
                            ? "border-primary bg-primary text-white shadow-md "
                            : "border-gray-200 hover:border-primary"
                        }`}
                    >
                      <input
                        type="radio"
                        name="selectedTime"
                        value={slot.start}
                        checked={formData.selectedTime === slot.start}
                        onChange={onChange}
                        className="sr-only"
                      />
                      <div className="text-sm font-semibold">
                        {convertTo12HourFormat(slot.start)}
                      </div>
                      {slot.availableAdmins > 0 && (
                        <div
                          className={`text-xs mt-1 ${
                            formData.selectedTime === slot.start
                              ? "text-white/80"
                              : "text-neutral-dark"
                          }`}
                        >
                          {slot.availableAdmins} staff
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              )}

              {/* DROPDOWN VIEW - Select element */}
              {timeViewMode === "dropdown" && (
                <div className="space-y-2">
                  <select
                    name="selectedTime"
                    value={formData.selectedTime || ""}
                    onChange={onChange}
                    className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white"
                  >
                    <option value="">Select a time slot</option>
                    {selectedDateSlots.map((slot) => (
                      <option key={slot.start} value={slot.start}>
                        {convertTo12HourFormat(slot.start)}
                        {slot.availableAdmins > 0
                          ? ` - ${slot.availableAdmins} staff available`
                          : ""}
                      </option>
                    ))}
                  </select>

                  {/* Selected time preview */}
                  {formData.selectedTime && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FiClock className="text-green-600" />
                        <div>
                          <p className="text-sm font-semibold text-green-900">
                            Selected Time
                          </p>
                          <p className="text-base font-bold text-green-700">
                            {convertTo12HourFormat(formData.selectedTime)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 bg-neutral-light rounded-lg">
              <FiClock className="mx-auto text-4xl text-neutral-dark mb-2" />
              <p className="text-neutral-dark font-medium mb-1">
                No available slots for this date
              </p>
              <p className="text-sm text-neutral-dark px-4">
                Try navigating to next week or adjust the duration
              </p>
            </div>
          )}

          {errors.selectedTime && (
            <p className="mt-2 text-sm text-red-500">{errors.selectedTime}</p>
          )}
        </div>
      )}

      {/* Helper Text - Mobile Optimized */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          💡 <strong>Smart Selection:</strong>{" "}
          <span className="hidden sm:inline">
            Select duration first, then today is auto-selected. If no slots
            available today, the closest available date is chosen. Toggle
            Grid/Dropdown for time slots. Navigate weeks to find more slots.
          </span>
          <span className="sm:hidden">
            Pick duration, then best date is auto-selected. Toggle
            Grid/Dropdown. Swipe to change weeks.
          </span>
        </p>
      </div>
    </div>
  );
};
